import { affiliateGroup } from './hydrate-referral.js'
import { decodeLandingToken } from './landing-experiment.js'

// A URL token is only decoded in the browser. Persist it after the authenticated
// backend confirms it is the immutable acquisition actually saved on the user.
export function accountLandingAssignment(token, result, now = Date.now()) {
  if (result?.status !== 'success') return null
  const assignment = decodeLandingToken(token, now)
  const saved = result.acquisition_experiments?.[assignment?.experiment_id]
  return assignment &&
    saved?.enrollment_id === assignment.enrollment_id &&
    saved?.variant === assignment.variant
    ? assignment
    : null
}

// A failed data load is unknown, not evidence that an account must be created.
export function accountDocumentStatus(data, httpStatus) {
  if (httpStatus === 404 || data?.isDefaultData === true) return 'missing'
  if (data?.status === 'success' && data.frontend_data) return 'ready'
  return 'unknown'
}

// Retrying a restored session only reads its existing account. A successful
// subscription lookup must not prevent recovery of a separate failed data load.
export async function fetchAccountDocument({
  endpoint,
  idToken,
  fetchImpl = fetch,
  timeoutMs = 15000,
}) {
  const controller = new AbortController()
  const timeout = setTimeout(() => controller.abort(), timeoutMs)
  try {
    const response = await fetchImpl(endpoint, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', Authorization: idToken },
      signal: controller.signal,
    })
    if (response.status === 404) return { data: null, status: 'missing' }
    if (!response.ok) throw new Error('Account data request failed')
    const data = await response.json()
    const status = accountDocumentStatus(data, response.status)
    if (status === 'unknown') throw new Error('Account data unavailable')
    return { data, status }
  } catch (cause) {
    throw new Error('Could not load your account. Please try again.', { cause })
  } finally {
    clearTimeout(timeout)
  }
}

// Finishing an already-started write is safe after navigation. UI cleanup and
// redirect belong only to the form that started it, and only to the same user.
export async function settleAccountCompletion({
  request,
  uid,
  getCurrentUid,
  applyAccount,
  isFormActive,
  onActiveSuccess,
}) {
  const result = await request()
  if (getCurrentUid() !== uid || !applyAccount(result)) return result
  if (isFormActive()) onActiveSuccess(result)
  return result
}

// Capture attribution once so a transient account-write failure cannot strip it
// from a retry. Firebase authentication alone does not mean an account exists.
//
// The Aiffiliate part (uid, tracker, landing, click reference) is one group:
// the stored one, else the one in the pc_ref cookie (`referral`, parsed) when
// localStorage was purged or is blocked, never keys of two different clicks.
// Promptchan's own /r/ uid (one-f) is sent in the same field when no Aiffiliate
// uid is known.
export function accountAttribution(
  readItem,
  landingExperimentToken,
  landingClicks = [],
  entry = null,
  referral = null,
) {
  const ref = readItem('ref')
  const affiliate = affiliateGroup(readItem, referral)
  return {
    aiffiliateUid: affiliate.aiffiliateUid || readItem('affiliateUid'),
    aiffiliateTracker: affiliate.aiffiliateTracker,
    aiffiliateClickRef: affiliate.aiffiliateClickRef,
    advertiser: readItem('advertiser') || null,
    visitorId: readItem('click_subid') || null,
    zoneId: readItem('click_zoneid') || null,
    campaignId: readItem('click_campaignid') || null,
    geoId: readItem('click_geo') || null,
    dynamicS2S: readItem('dynamicS2S') || null,
    ref: ref || null,
    via: readItem('via') || null,
    landing: affiliate.landing,
    cr_transaction_id: ref === 'cr' ? readItem('cr_transaction_id') : null,
    cr_affiliate_id: ref === 'cr' ? readItem('cr_affiliate_id') : null,
    cr_source: ref === 'cr' ? readItem('cr_source') : null,
    landing_experiment_token: landingExperimentToken || null,
    // where they first came from (entry-source.js)
    ...(entry ? { entry } : {}),
    ...(landingExperimentToken && landingClicks.length
      ? { landing_experiment_clicks: landingClicks }
      : {}),
  }
}

// Every localStorage key accountAttribution reads, and the sign-in email: cleared
// once an account is created, so a later sign-up in this browser is not credited
// with them. (Until 7 October 2026 landing, the click_* and dynamicS2S keys and
// two of the three cr_* keys were left behind.)
export const ACCOUNT_ATTRIBUTION_KEYS = [
  'affiliateUid',
  'aiffiliateUid',
  'aiffiliateTracker',
  'aiffiliateClickRef',
  'landing',
  'advertiser',
  'click_subid',
  'click_zoneid',
  'click_campaignid',
  'click_geo',
  'dynamicS2S',
  'ref',
  'via',
  'cr_transaction_id',
  'cr_affiliate_id',
  'cr_source',
  'emailForSignIn',
]

export async function completeAccount({
  endpoint,
  idToken,
  attribution,
  fetchImpl = fetch,
  wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
  attempts = 2,
  retryDelay = 3000,
  timeoutMs = 15000,
}) {
  const body = JSON.stringify(attribution)
  let cause
  for (let attempt = 0; attempt < attempts; attempt++) {
    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), timeoutMs)
    try {
      const response = await fetchImpl(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: idToken },
        body,
        signal: controller.signal,
      })
      const result = await response.json()
      if (response.ok && result.status === 'success') return result
      cause = new Error(result.error || 'Account request failed')
    } catch (error) {
      cause = error
    } finally {
      clearTimeout(timeout)
    }
    if (attempt < attempts - 1) await wait(retryDelay)
  }
  throw new Error('Could not finish signing you in. Please try again.', {
    cause,
  })
}
